"use client"

import { useState, useTransition } from "react"
import { HttpTypes } from "@medusajs/types"

import { ListingZone, listProductsWithSort } from "@lib/data/products"
import { CatalogFilters } from "@lib/util/product-specs"
import { UsedFilters } from "@lib/util/used-listing"
import ProductPreview from "@modules/products/components/product-preview"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"

type QueryParams = {
  limit: number
  collection_id?: string[]
  category_id?: string[]
  id?: string[]
  order?: string
  q?: string
}

type ProductGridLoadMoreProps = {
  initialProducts: HttpTypes.StoreProduct[]
  region: HttpTypes.StoreRegion
  count: number
  countryCode: string
  sortBy?: SortOptions
  filters?: CatalogFilters
  queryParams: QueryParams
  pageSize?: number
  zone?: ListingZone
  usedFilters?: UsedFilters
}

export default function ProductGridLoadMore({
  initialProducts,
  region,
  count,
  countryCode,
  sortBy,
  filters,
  queryParams,
  pageSize = 12,
  zone = "catalog",
  usedFilters,
}: ProductGridLoadMoreProps) {
  const [products, setProducts] = useState(initialProducts)
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()

  const hasMore = products.length < count

  const handleLoadMore = () => {
    if (isPending || !hasMore) return
    setError(null)

    startTransition(async () => {
      try {
        const nextPage = page + 1
        const {
          response: { products: nextProducts },
        } = await listProductsWithSort({
          page: nextPage,
          queryParams: { ...queryParams, limit: pageSize },
          sortBy,
          countryCode,
          filters,
          zone,
          usedFilters,
        })

        if (!nextProducts.length) {
          setPage(nextPage)
          return
        }

        setProducts((prev) => {
          const seen = new Set(prev.map((p) => p.id))
          const appended = nextProducts.filter((p) => !seen.has(p.id))
          return [...prev, ...appended]
        })
        setPage(nextPage)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "โหลดสินค้าเพิ่มไม่สำเร็จ"
        )
      }
    })
  }

  return (
    <div>
      <ul
        className="grid w-full grid-cols-1 gap-5 xsmall:grid-cols-2 small:grid-cols-3 medium:grid-cols-4 small:gap-6"
        data-testid="products-list"
      >
        {products.map((p) => (
          <li key={p.id} className="h-full">
            <ProductPreview product={p} region={region} />
          </li>
        ))}
      </ul>

      {hasMore && (
        <div className="mt-12 flex flex-col items-center gap-3">
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isPending}
            data-testid="product-load-more"
            className="inline-flex min-w-[10rem] items-center justify-center rounded-lg border border-[#d7dadd] bg-white px-6 py-3 text-sm font-medium text-mt-ink transition hover:border-vx-accent hover:text-vx-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isPending ? "กำลังโหลด…" : "โหลดเพิ่ม"}
          </button>
        </div>
      )}
    </div>
  )
}
